import type { EnabledNotificationTypesDict } from '../../../hooks/useGetProfile';
import type { NotificationType } from '../../../hooks/useGetNotifications';

/** Types that do NOT have email support. */
export const EMAIL_EXCLUDED_TYPES: NotificationType[] = ['order-placement-failed'];

/** CCC-only options: shown for CCC orgs, hidden for Nexsyis orgs (no CCC purchase orders). */
export const CCC_ONLY_TYPES: NotificationType[] = ['ccc-manual-po-created', 'ccc-manual-po-failed'];

/**
 * Invoicing / Nexsyis-only options: shown when the user can see flagged invoices
 * (Nex + invoicing). Includes negotiation handoff from ENG-2315.
 */
export const INVOICING_ONLY_TYPES: NotificationType[] = ['flagged-invoices', 'negotiation-handoff'];

/** Only sent to location-scoped roles (central-parts / parts-manager / executive), never to estimators/GMs. */
export const CENTRAL_PARTS_ONLY_TYPES: NotificationType[] = [
    'quotes-back',
    'quotes-back-potential-total',
    'quotes-back-quotes-only',
    'no-quoted-parts',
    'flagged-parts',
    'no-oem-quotes',
    'order-request-enabled',
];

/** Re-key types only relevant when rekeying IS enabled. */
export const REKEY_ENABLED_ONLY_TYPES: NotificationType[] = [
    'rekeying-completed',
    'rekeying-completed-flagged',
    'rekeying-skipped',
    'rekey-advisor-score',
    'rekey-quote-only-completed',
];

/**
 * `needs-rekey` stays visible even when rekeying is enabled: it still fires for
 * non-DRP jobs (when shouldRekeyNonDrp is off) and excluded makes.
 */
export const NO_HIDDEN_REKEY_TYPES: NotificationType[] = [];

export const ALL_NOTIFICATION_TYPES: NotificationType[] = [
    'new-order',
    'quotes-extended',
    'auto-requote',
    'quotes-back',
    'quotes-back-potential-total',
    'quotes-back-quotes-only',
    'no-oem-quotes',
    'back-order-ordered',
    'no-quoted-parts',
    'flagged-parts',
    'pre-order-backorder',
    'tesla-order-error',
    'part-number-changed',
    'orders-confirmed',
    'orders-rejected',
    'orders-returned-cancelled',
    'supplier-changes',
    'order-not-confirmed',
    'rekeying-completed',
    'rekeying-completed-flagged',
    'rekeying-skipped',
    'needs-rekey',
    'rekey-advisor-score',
    'order-request-enabled',
    'order-placement-failed',
    'ccc-manual-po-created',
    'ccc-manual-po-failed',
    'flagged-invoices',
    'negotiation-handoff',
    'rekey-quote-only-completed',
    'inventory-stock-request',
    'inventory-stock-request-placed',
    'order-mention',
    'invoice-mention',
    'vendor-message',
];

export interface NotificationCategoryType {
    type: NotificationType;
    label: string;
    /** Optional secondary text shown under the label in the events table. */
    description?: string;
    /** Optional hover tooltip shown via an info icon next to the label. */
    tooltip?: string;
}

export interface NotificationCategory {
    header: string;
    types: NotificationCategoryType[];
}

export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
    {
        header: 'Requesting Parts',
        types: [
            { type: 'new-order', label: 'New Order' },
            { type: 'quotes-extended', label: 'Quotes Extended' },
            { type: 'auto-requote', label: 'Auto Re-Quote' },
            { type: 'quotes-back', label: 'Quotes Back' },
            // Granular quotes-back sub-types: when enabled, these replace the
            // global Quotes Back notification for orders carrying that mark.
            {
                type: 'quotes-back-potential-total',
                label: 'Quotes Back (Potential Total)',
                tooltip:
                    'Replaces Quotes Back for orders marked potential total.',
            },
            {
                type: 'quotes-back-quotes-only',
                label: 'Quotes Back (Quotes Only)',
                tooltip: 'Replaces Quotes Back for orders marked quotes only.',
            },
            { type: 'no-oem-quotes', label: 'No OEM Quotes' },
        ],
    },
    {
        header: 'Ready to Order',
        types: [
            { type: 'no-quoted-parts', label: 'No Quotes' },
            { type: 'flagged-parts', label: 'Flagged Parts' },
            { type: 'order-request-enabled', label: 'Order Request Enabled' },
            { type: 'pre-order-backorder', label: 'Pre-Order Backorder' },
            { type: 'tesla-order-error', label: 'Tesla Error' },
            { type: 'part-number-changed', label: 'Part Number Change' },
        ],
    },
    {
        header: 'Ordered',
        types: [
            { type: 'back-order-ordered', label: 'Backordered Parts Ordered' },
            { type: 'orders-confirmed', label: 'Confirmed' },
            { type: 'orders-rejected', label: 'Rejected' },
            { type: 'orders-returned-cancelled', label: 'Return / Cancellation' },
            { type: 'supplier-changes', label: 'Supplier Changes' },
            { type: 'order-not-confirmed', label: 'Not Confirmed' },
            { type: 'order-placement-failed', label: 'Order Failed' },
            { type: 'ccc-manual-po-created', label: 'CCC Manual PO Created' },
            { type: 'ccc-manual-po-failed', label: 'CCC Manual PO Failed' },
            { type: 'flagged-invoices', label: 'Flagged Invoice' },
            {
                type: 'negotiation-handoff',
                label: 'Negotiation Needs Reply',
                description: 'An invoice price negotiation was handed off and needs a user to reply.',
            },
            { type: 'inventory-stock-request', label: 'Purchase Order' },
            { type: 'inventory-stock-request-placed', label: 'In Stock Ordered' },
        ],
    },
    {
        header: 'Re-Key',
        types: [
            { type: 'rekeying-completed', label: 'Re-key done' },
            { type: 'rekeying-completed-flagged', label: 'Re-key flagged' },
            { type: 'rekeying-skipped', label: 'Re-key skipped' },
            { type: 'needs-rekey', label: 'Re-key needed' },
            { type: 'rekey-advisor-score', label: 'Advisor score alert' },
            { type: 'rekey-quote-only-completed', label: 'Re-key quote-only done' },
        ],
    },
    {
        header: 'Messages',
        types: [
            { type: 'order-mention', label: 'Order messages & @mentions' },
            { type: 'invoice-mention', label: 'Invoice comments & @mentions' },
            { type: 'vendor-message', label: 'Vendor messages' },
        ],
    },
];

/**
 * Older saved preference dicts predate `order-mention`. In-app message/mention
 * notifications default ON server-side when the key is absent, so reflect that
 * here (email stays off until the user enables it).
 */
/** Older saved preference dicts predate invoice comments. An absent key stays on. */
export const withInvoiceMentionDefault = (
    dict: EnabledNotificationTypesDict,
): EnabledNotificationTypesDict => {
    const next = { ...dict };
    if (next['invoice-mention'] === undefined) next['invoice-mention'] = ['notif', 'email'];
    return next;
};

export const withMessageDefaults = (
    dict: EnabledNotificationTypesDict,
): EnabledNotificationTypesDict => {
    const next = { ...dict };
    if (next['order-mention'] === undefined) next['order-mention'] = ['notif'];
    if (next['vendor-message'] === undefined) next['vendor-message'] = ['notif'];
    return next;
};

/**
 * Types that default to in-app and email on for anyone who requests quotes, as the
 * API does. A missing key is both channels on; an explicit `[]` stays off.
 * Invoicing and technician roles don't see these toggles, so they get no default.
 */
export const QUOTER_DEFAULT_TYPES: NotificationType[] = ['part-number-changed', 'needs-rekey'];

export const withQuoterDefaults = (
    dict: EnabledNotificationTypesDict,
    position?: string,
): EnabledNotificationTypesDict => {
    if (position === 'invoicing' || position === 'technician') return dict;
    const missing = QUOTER_DEFAULT_TYPES.filter((type) => dict[type] === undefined);
    if (missing.length === 0) return dict;
    const next = { ...dict };
    for (const type of missing) next[type] = ['notif', 'email'];
    return next;
};

/** Default preferences dict (everything enabled). */
export const buildDefaults = (): EnabledNotificationTypesDict => {
    const dict: EnabledNotificationTypesDict = {};
    for (const type of ALL_NOTIFICATION_TYPES) {
        dict[type] = EMAIL_EXCLUDED_TYPES.includes(type) ? ['notif'] : ['notif', 'email'];
    }
    return dict;
};

export const withFlaggedInvoiceDefault = (
    dict: EnabledNotificationTypesDict,
    invoicingCapable: boolean,
): EnabledNotificationTypesDict => {
    const next = { ...dict };
    const flagged = next['flagged-invoices'];
    if (!flagged || flagged.length === 0) {
        next['flagged-invoices'] = invoicingCapable ? ['notif', 'email'] : ['notif'];
    }
    return next;
};

/**
 * Older saved preference dicts predate `negotiation-handoff`. Delivery treats an
 * absent key as unmuted; mirror that in settings so checkboxes match toasts/bell.
 */
export const withNegotiationHandoffDefault = (
    dict: EnabledNotificationTypesDict,
    invoicingCapable: boolean,
): EnabledNotificationTypesDict => {
    const next = { ...dict };
    const handoff = next['negotiation-handoff'];
    // Only fill absent keys. An explicit `[]` means the user disabled every channel.
    if (handoff === undefined) {
        next['negotiation-handoff'] = invoicingCapable ? ['notif', 'email'] : ['notif'];
    }
    return next;
};

/** An absent or empty dict means "never customized", which is all-on. */
export const resolvePrefs = (
    dict: EnabledNotificationTypesDict | undefined,
    options?: { invoicingCapable?: boolean; position?: string },
): EnabledNotificationTypesDict => {
    const base = withInvoiceMentionDefault(
        dict && Object.keys(dict).length > 0 ? withMessageDefaults(dict) : buildDefaults(),
    );
    const withInvoicing = options?.invoicingCapable === undefined
        ? base
        : withNegotiationHandoffDefault(
            withFlaggedInvoiceDefault(base, options.invoicingCapable),
            options.invoicingCapable,
        );
    return withQuoterDefaults(withInvoicing, options?.position);
};

export interface NotificationVisibilityContext {
    position?: string;
    managementSystem?: 'ccc' | 'nex';
    invoicingEnabled: boolean;
    invoicingCapable?: boolean;
    rekeyingEnabled: boolean;
    organizationMode?: 'full-time' | 'invoicing' | 'inventory';
}

const canSeeFlaggedInvoices = (ctx: NotificationVisibilityContext): boolean =>
    ctx.managementSystem === 'nex' &&
    ctx.invoicingEnabled &&
    (ctx.invoicingCapable === true ||
        ctx.position === 'invoicing' ||
        ctx.organizationMode === 'invoicing');

export const isNotificationTypeVisible = (
    type: NotificationType,
    ctx: NotificationVisibilityContext,
): boolean => {
    if (ctx.organizationMode === 'invoicing' || ctx.position === 'invoicing') {
        return (
            (type === 'flagged-invoices' || type === 'negotiation-handoff' || type === 'invoice-mention') &&
            canSeeFlaggedInvoices(ctx)
        );
    }
    if (ctx.position === 'technician') return false;

    const isLocationRole = ctx.position === 'central-parts' || ctx.position === 'parts-manager' || ctx.position === 'executive';
    if (!isLocationRole && CENTRAL_PARTS_ONLY_TYPES.includes(type)) return false;

    const hiddenRekeyTypes = ctx.rekeyingEnabled ? NO_HIDDEN_REKEY_TYPES : REKEY_ENABLED_ONLY_TYPES;
    if (hiddenRekeyTypes.includes(type)) return false;

    if (CCC_ONLY_TYPES.includes(type) && ctx.managementSystem === 'nex') return false;
    if (INVOICING_ONLY_TYPES.includes(type) && !canSeeFlaggedInvoices(ctx)) return false;

    return true;
};

export const visibleNotificationCategories = (ctx: NotificationVisibilityContext) =>
    NOTIFICATION_CATEGORIES.map((category) => ({
        ...category,
        types: category.types.filter(({ type }) => isNotificationTypeVisible(type, ctx)),
    })).filter((category) => category.types.length > 0);

export const dictsEqual = (
    a: EnabledNotificationTypesDict,
    b: EnabledNotificationTypesDict,
): boolean => {
    const aKeys = Object.keys(a) as NotificationType[];
    const bKeys = Object.keys(b) as NotificationType[];
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
        const aChannels = a[key] ?? [];
        const bChannels = b[key] ?? [];
        if (aChannels.length !== bChannels.length) return false;
        if (aChannels.some((c) => !bChannels.includes(c))) return false;
    }
    return true;
};
